<template>
  <WidgetCadre
    titre="Tickets"
    v-bind="apparence"
    :lien="donnees?.lien || '/support/tickets'"
    :libelle-lien="`Tout voir${donnees?.ouverts ? ' (' + donnees.ouverts + ')' : ''}`"
    :chargement="chargement"
    :panne="panne"
    :reglable="reglable"
    :etat="etat"
    @retirer="$emit('retirer')"
    @recharger="$emit('recharger')"
  >
    <dl class="tickets__compteurs">
      <div
        v-for="compteur in compteurs"
        :key="compteur.libelle"
      >
        <dd class="chiffre chiffre--moyen">
          {{ compteur.valeur ?? '—' }}
        </dd>
        <dt class="etiquette">
          {{ compteur.libelle }}
        </dt>
      </div>
    </dl>

    <div class="tickets__lignes">
      <button
        v-for="ticket in lignes"
        :key="ticket.id"
        class="ticket surface"
        type="button"
        @click="router.push(`/support/tickets/${ticket.id}`)"
      >
        <span
          class="ticket__priorite"
          :class="`ticket__priorite--${ticket.priorite}`"
          :title="`Priorité ${ticket.priorite}`"
        />
        <span class="ticket__objet">{{ ticket.objet || ticket.reference }}</span>
        <span
          v-if="ticket.a_moi"
          class="ticket__moi"
        >à moi</span>
        <span class="ticket__guichet">{{ ticket.guichet }}</span>
      </button>

      <p
        v-if="!lignes.length"
        class="etiquette"
      >
        Aucun ticket ouvert sur vos guichets.
      </p>
    </div>
  </WidgetCadre>
</template>

<script setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import WidgetCadre from './WidgetCadre.vue'
import { apparenceDe } from './apparence'

/**
 * Les tickets des guichets de cette personne : ce qui attend une prise en
 * charge, ce qui n'a jamais eu de réponse, et ce qui lui est assigné.
 *
 * Les compteurs portent sur tous les tickets ouverts visibles, pas sur les
 * lignes affichées ; les lignes mettent en tête ceux qui lui sont assignés.
 */

const props = defineProps({
    donnees: { type: Object, default: null },
    chargement: { type: Boolean, default: false },
    panne: { type: Boolean, default: false },
    reglable: { type: Boolean, default: false },
})

defineEmits(['retirer', 'recharger'])

const apparence = apparenceDe('tickets')
const router = useRouter()

const lignes = computed(() => props.donnees?.lignes ?? [])

const compteurs = computed(() => [
    { libelle: 'à traiter', valeur: props.donnees?.a_traiter },
    { libelle: 'sans réponse', valeur: props.donnees?.sans_reponse },
    { libelle: 'à moi', valeur: props.donnees?.a_moi },
])

/** Une demande jamais répondue est ce qui se paie en satisfaction : c'est elle qui allume l'anneau. */
const etat = computed(() => {
    if (!props.donnees) return 'repos'

    return props.donnees.sans_reponse > 0 ? 'attention' : 'sain'
})
</script>

<style lang="scss" scoped>
.tickets {
  &__compteurs {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
    margin: 0 0 10px;

    dt, dd { margin: 0; }
  }

  &__lignes {
    display: flex;
    flex-direction: column;
    gap: 0;
  }
}

.ticket {
  display: flex;
  align-items: center;
  gap: 9px;
  width: 100%;
  padding: 8px 10px;
  border: none;
  cursor: pointer;
  text-align: left;


  /* Un filet entre les lignes plutôt qu'un aplat par ligne : la liste se lit
     d'un trait, et le survol seul désigne ce qui est cliquable. En bordure et
     non en ombre portée : l'ombre servirait le même canal que le repère « en
     retard », et l'effacerait sur toute ligne qui en suit une autre. */
  & + & { border-top: 1px solid var(--el-border-color-lighter); }

  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 1px;
  }

  &__priorite {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--el-text-color-placeholder);
    flex-shrink: 0;

    &--haute { background: var(--teinte-ambre); }
    &--urgente { background: var(--teinte-corail); }
  }

  &__objet {
    flex: 1;
    min-width: 0;
    font-size: 13px;
    color: var(--el-text-color-primary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__moi {
    padding: 1px 7px;
    border-radius: 999px;
    background: color-mix(in srgb, var(--accent) 16%, transparent);
    color: var(--encre);
    font-size: 11px;
    font-weight: 600;
    flex-shrink: 0;
  }

  &__guichet {
    font-size: 11.5px;
    color: var(--el-text-color-secondary);
    flex-shrink: 0;
    max-width: 110px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>
